import { htmlEncode } from '../../utils/escape.utils';
import {
  CompanyRiskScoreCategories,
  DoubleValueWithOptionalDelta,
  GetCompanyRiskScoreResponse,
  Status,
} from '@nirvana/shared/dtos/types/widgets/common';
import { formatPercentages, formatPercentageValue } from '../../utils/formatter.utils';
import { BaseWidgetView } from '../../widget/BaseWidgetView';
import { DisplayMode, TemplateState } from '../../types/common/types';
import { BackendResponseWithMeta, DASHBOARD_METADATA_KEY } from '../../types/widget/types';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import {
  RiskManagementNavigationParams,
  NavigationConstants,
  NavigationHelper,
  NavigationItem,
} from '@nirvana/shared/navigation';
import { exitFullscreen, isBrowserFullscreen, onExitFullscreenError } from '../../utils/fullscreen.utils';
import { GzTextWebComponent } from '../../ui/gzText';

const ATTRIBUTE_NAME = 'gz-risk-score';

export class CompanyRiskScoreView extends BaseWidgetView {
  public shadow: any;
  protected translations: any;
  private $listContainer: HTMLElement;
  private navigationHandlers: Array<(event: any) => void> = [];

  getDomElements() {
    super.getDomElements();
    this.$listContainer = this.shadow.getElementById('companyRiskList');
    this.dom.$widgetTitle = this.shadow.querySelector('.widget-title');
    this.dom.$widgetTitleString = this.shadow.querySelector('#companyRiskTitle');
    this.dom.$gzGauge = this.shadow.querySelector('#companyRiskGauge');
    this.dom.$missingLicense = this.shadow.querySelector('#missingLicense') as GzTextWebComponent;
    this.getTranslations();
    this.setTranslatedStrings();
    this.setErrorTooltip();
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getCompanyRiskScoreWidgetStrings();
  }

  setTranslatedStrings() {
    const { title, missingYearlyLicense } = this.translations;
    this.dom.$widgetTitleString.innerHTML = title;
    this.dom.$missingLicense.innerHTML = missingYearlyLicense;
  }

  buildSuccessView(response: BackendResponseWithMeta<GetCompanyRiskScoreResponse>) {
    this.resetTemplateState(this.$wrapper);

    const result = response.widgetResult;

    if (result.status === Status.MISSING_LICENSE) {
      this.setTemplateState(this.$wrapper, TemplateState.MISSING);
      this.dom.$gzGauge.disabled = true;
      return;
    }

    if (result.status === Status.EMPTY_STATE) {
      this.setTemplateState(this.$wrapper, TemplateState.EMPTY);
      this.buildEmptyTemplate();
      return;
    }

    if (result.status === Status.ACTIVE && result.data) {
      const data = result.data;
      this.buildList(data.categories);

      switch (response[DASHBOARD_METADATA_KEY].displayMode) {
        case DisplayMode.PDF:
          this.dom.$gzGauge.animations = false;
          break;
        case DisplayMode.DASHBOARD:
        case DisplayMode.REPORT:
          this.dom.$gzGauge.animations = true;
          break;
        default:
          throw new Error('Should not reach');
      }

      this.dom.$gzGauge.disabled = false;
      this.dom.$gzGauge.value = formatPercentageValue(data.companyRiskScore);
      this.addDrillDownHandlers(
        response[DASHBOARD_METADATA_KEY].targets,
        response[DASHBOARD_METADATA_KEY].canDrillDown
      );
      return;
    }

    this.buildErrorView();
  }

  buildErrorView() {
    this.resetTemplateState(this.$wrapper);
    this.setTemplateState(this.$wrapper, TemplateState.ERROR);
    this.buildEmptyTemplate();
  }

  private buildEmptyTemplate() {
    const categories = {
      appVulnerabilities: {
        currentValue: 0,
      },
      humanRisk: {
        currentValue: 0,
      },
      misconfiguration: {
        currentValue: 0,
      },
    };
    const companyRiskScore = 0;

    this.buildList(categories);
    this.dom.$gzGauge.value = formatPercentageValue(companyRiskScore);
  }

  private buildList(categories: CompanyRiskScoreCategories) {
    Object.entries(categories).forEach(([categoryName, categoryValues], index) => {
      this.buildListElement(categoryName, categoryValues, index);
    });
  }

  private buildListElement(categoryName: string, categoryValues: DoubleValueWithOptionalDelta, index: number) {
    const $listElement = this.$listContainer.children[index];
    $listElement.setAttribute(ATTRIBUTE_NAME, categoryName);
    const { categoriesMap } = this.translations;

    const $name = $listElement.querySelector(`[gz-list-element="name"]`);
    // @ts-ignore
    $name.textContent = categoriesMap[categoryName];
    const $value = $listElement.querySelector(`[gz-list-element="value"]`);
    // @ts-ignore
    $value.textContent = htmlEncode(`${formatPercentageValue(categoryValues.currentValue)}%`);
    const $delta = $listElement.querySelector(`[gz-list-element="delta"]`);

    if (!categoryValues.delta) {
      // @ts-ignore
      $delta.textContent = '';
      $delta?.classList.add('hidden');
      return;
    }

    // @ts-ignore
    $delta.textContent = htmlEncode(formatPercentages(categoryValues.delta));

    this.applyDeltaClass($delta, categoryValues.delta, false);
  }

  private addDrillDownHandlers(targets: Array<Target>, canDrillDown: boolean) {
    if (!NavigationHelper.shouldAddHandlers(this.dependencies.Ext)) {
      return;
    }

    if (!targets || targets.length === 0) {
      return;
    }

    const company = {
      id: targets[0].companyId,
      name: targets[0].companyName,
    };

    let navigationItems: Array<NavigationItem> = [
      {
        elements: [this.dom.$widgetTitle],
        section: NavigationConstants.RISK_MANAGEMENT.NAME,
        params: <RiskManagementNavigationParams>{
          company,
        },
      },
      {
        elements: [this.shadow.querySelector(`[${ATTRIBUTE_NAME}="misconfiguration"] .item-details`)],
        section: NavigationConstants.RISK_MANAGEMENT.NAME,
        params: <RiskManagementNavigationParams>{
          company,
          section: NavigationConstants.RISK_MANAGEMENT.SUBSECTIONS.MISCONFIGURATIONS,
        },
      },
      {
        elements: [this.shadow.querySelector(`[${ATTRIBUTE_NAME}="appVulnerabilities"] .item-details`)],
        section: NavigationConstants.RISK_MANAGEMENT.NAME,
        params: <RiskManagementNavigationParams>{
          company,
          section: NavigationConstants.RISK_MANAGEMENT.SUBSECTIONS.VULNERABILITIES,
        },
      },
      {
        elements: [this.shadow.querySelector(`[${ATTRIBUTE_NAME}="humanRisk"] .item-details`)],
        section: NavigationConstants.RISK_MANAGEMENT.NAME,
        params: <RiskManagementNavigationParams>{
          company,
          section: NavigationConstants.RISK_MANAGEMENT.SUBSECTIONS.USER_BEHAVIOR_RISKS,
        },
      },
    ];

    navigationItems = NavigationHelper.removeUnnavigableItems(this.dependencies.Ext, navigationItems);

    let navigationHandler;
    if (canDrillDown) {
      navigationHandler = (event, navigationParams, section) => {
        if (BaseWidgetView.isSuccessState(this.$wrapper)) {
          if (isBrowserFullscreen()) {
            exitFullscreen(() => {}, onExitFullscreenError.bind(this));
          }

          switch (section) {
            case NavigationConstants.RISK_MANAGEMENT.NAME:
              NavigationHelper.navigateToRiskManagement(navigationParams as RiskManagementNavigationParams);
              break;
            default:
              throw new Error('Trying to navigate to invalid console module');
          }
        }
      };
    }

    NavigationHelper.updateNavigableItems(navigationItems, this.navigationHandlers, navigationHandler);
  }
}
